/**
 * Subject Creation Form
 *
 * Mirrors TeacherForm.tsx shape (UI Standards). Subject is per-school;
 * for school-admins/consultants the school is auto-set from their teacher
 * profile. Platform admins can pick any school.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { SubjectCreate } from '../../types/subject';

const SubjectForm: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [schools, setSchools] = useState<any[]>([]);

  const userSchoolId = user?.user_profile?.teacher_profile?.school?.id || 0;

  const [formData, setFormData] = useState<SubjectCreate>({
    name: '',
    short_name: '',
    school: userSchoolId,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchSchools = async () => {
      // Only platform admins need the school picker; everyone else's school
      // is auto-set from their teacher profile.
      if (!isAdmin) {
        setLoadingData(false);
        return;
      }
      try {
        const res = await axios.get('/api/schools/');
        setSchools(res.data.results || res.data || []);
      } catch (err) {
        console.error('Error loading schools:', err);
        setError('Failed to load schools');
      } finally {
        setLoadingData(false);
      }
    };
    fetchSchools();
  }, [isAdmin]);

  // Keep school in sync once user loads
  useEffect(() => {
    if (!isAdmin && userSchoolId && formData.school !== userSchoolId) {
      setFormData((prev) => ({ ...prev, school: userSchoolId }));
    }
  }, [isAdmin, userSchoolId, formData.school]);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Name is required';
    }
    if (!formData.school || formData.school === 0) {
      errors.school = 'School is required';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/subjects/', {
        name: formData.name.trim(),
        short_name: (formData.short_name || '').trim(),
        school: formData.school,
      });
      setSuccess(true);
      setTimeout(() => navigate('/subjects'), 1200);
    } catch (err: any) {
      console.error('Error creating subject:', err);
      const data = err.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const msg = Object.entries(data)
          .map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(', ') : m}`)
          .join('; ');
        setError(msg || 'Failed to create subject.');
      } else {
        setError('Failed to create subject. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate('/subjects');

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/subjects')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Subjects
        </MuiLink>
        <Typography color="text.primary">New Subject</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Create New Subject</Typography>
        <Typography variant="body2" color="textSecondary">
          Subjects are per-school. They are seeded from Adam during class sync; you can also add or
          edit them here.
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Subject created. Redirecting…
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name || 'e.g. "Information Technology"'}
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Short name"
                name="short_name"
                value={formData.short_name || ''}
                onChange={handleChange}
                disabled={loading}
                helperText='Optional code, e.g. "IT". Used by Adam.'
              />
            </Stack>

            {isAdmin && (
              <FormControl fullWidth required error={!!validationErrors.school}>
                <InputLabel>School</InputLabel>
                <Select
                  name="school"
                  value={formData.school || ''}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, school: Number(e.target.value) }));
                    if (validationErrors.school) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.school;
                        return next;
                      });
                    }
                  }}
                  label="School"
                  disabled={loading || loadingData}
                >
                  {schools.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
                {validationErrors.school && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {validationErrors.school}
                  </Typography>
                )}
              </FormControl>
            )}
            {!isAdmin && (
              <Typography variant="caption" color="textSecondary">
                School: <strong>{user?.user_profile?.teacher_profile?.school?.name || '—'}</strong> (auto-assigned)
              </Typography>
            )}

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Creating…' : 'Create Subject'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default SubjectForm;
